import { Component, Inject, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { UntypedFormControl, Validators } from "@angular/forms";
import { forkJoin, map, Observable, startWith } from "rxjs";
import { optionValidator, ReaderService } from "../../services/reader.service";
import {
  Cycle,
  Messages,
  MyErrorStateMatcher,
  Region,
  Vehicle,
} from "../utility/utility-classes";
import { WriterService } from "../../services/writer.service";
import { AuthenticationService } from "../authentication/authentication/authentication.service";
import { ErrorHandlerDialogComponent } from "../error-handler-dialog/error-handler-dialog.component";
import { ConfirmDeletionDialogComponent } from "../utility/confirm-deletion-dialog/confirm-deletion-dialog.component";

@Component({
    selector: "app-new-bodycycle",
    templateUrl: "./new-bodycycle.component.html",
    styleUrls: ["./new-bodycycle.component.scss"],
    standalone: false
})
export class NewBodycycleComponent implements OnInit {
  matcher = new MyErrorStateMatcher();
  bodystyle: BodyStyle = new BodyStyle();
  vehicle: Vehicle;
  bodyControl = new UntypedFormControl("", [Validators.required]);
  bodystyles: string[];
  filteredBodys: Observable<string[]>;
  cycles: Cycle[];
  regions: Region[] = [];
  regionControl = false;
  cycle1 = false;
  cycle2 = false;
  cycle3 = false;
  cycle4 = false;
  cycle5 = false;
  counter = [1980, 1980, 1980, 1980, 1980];
  isLoading = false;

  vehicleControl = new UntypedFormControl("", [Validators.required]);
  filteredVehicles: Observable<string[]>;
  vehicles: string[];
  startEngCodes: string[] = [
    "New",
    "Major",
    "Major*",
    "MCM",
    "MCM*",
    "Add",
    "Add*",
    "",
  ];
  midEngCodes: string[] = ["MCM", "MCE2", ""];
  endEngCodes: string[] = ["End", "Drop", ""];
  editMode = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<NewBodycycleComponent>,
    public confirmDialog: MatDialogRef<ConfirmDeletionDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly readerService: ReaderService,
    private readonly writerService: WriterService,
    private readonly authService: AuthenticationService
  ) {
    this.editMode = data.editMode;
    if (data.entry != null) {
      if (data.entry.vehicle != null) {
        this.vehicle = data.entry.vehicle;
        this.bodystyle.status = "Confirmed";
      }
      if (data.entry.record != null) {
        this.bodystyle = data.entry.record;
        this.bodyControl.setValue(this.bodystyle.bodystyle);
        if (data.entry.regions != null) {
          this.regions = data.entry.regions;
        }
        this.cycles = data.entry.cycles;
      }
    }
  }

  ngOnInit(): void {
    this.bodyControl.markAsTouched();
    if (this.cycles == null || this.cycles.length < 5) {
      this.cycles = [
        new Cycle(null, null),
        new Cycle(null, null),
        new Cycle(null, null),
        new Cycle(null, null),
        new Cycle(null, null),
      ];
    }
    this.readerService
      .getAllEnabledFromTable("bodystylevalue")
      .subscribe((x: any[]) => {
        this.bodystyles = x.map((obj) => obj.name);
        this.bodyControl.setValidators(optionValidator(this.bodystyles));
        this.filteredBodys = this.bodyControl.valueChanges.pipe(
          startWith(""),
          map((value) =>
            this.readerService._filterAutoComplete(value, this.bodystyles)
          )
        );
      });
    const set: Set<string> = new Set(this.regions.map((reg) => reg.regionname));
    this.readerService.getRegions("bodystyle").subscribe((x: any[]) => {
      this.regions = x.map(
        (y) =>
          new Region(this.vehicle.id, y.split(",")[1], set.has(y.split(",")[1]))
      );
    });

    // Only required when creating entry from homepage.
    if (this.vehicle == null) {
      this.vehicle = new Vehicle();
      this.readerService.getDistinctVehicles().subscribe((x: any) => {
        this.vehicles = x.map((obj) => obj.id + " - " + obj.nameplate);
        this.vehicleControl.setValidators(optionValidator(this.vehicles));
        this.filteredVehicles = this.vehicleControl.valueChanges.pipe(
          startWith(""),
          map((value) =>
            this.readerService._filterAutoComplete(value, this.vehicles)
          )
        );
      });
    }
  }

  public save(): void {
    this.isLoading = true;
    if (this.bodystyle.lead == null) {
      this.bodystyle.lead = false;
    }
    this.bodystyle.vehicleid = this.vehicle.id;
    this.bodystyle.bodystyle = this.bodyControl.value;
    this.bodystyle.created = new Date().toISOString();
    this.bodystyle.createdby = this.authService.userInfo.userName;

    if (!this.editMode) {
      this.bodystyle.id = undefined;
    }

    //Create array of observables
    const observableCalls: Observable<any>[] = [];

    this.writerService
      .processRecord(this.bodystyle, "bodystyle", "POST")
      .subscribe({
        next: (res: any) => {
          this.bodystyle.id = res.id;
          this.regions.forEach((region) => {
            region.bodystyleid = this.bodystyle.id;
            region.created = this.bodystyle.created;
          });
          const xRegions: Region[] = this.regions.filter(
            (region: Region) => region.value
          );

          this.cycles.forEach((cycle: Cycle) => {
            cycle.bodystyleid = this.bodystyle.id;
            cycle.created = this.bodystyle.created;
          });
          const xCycles: Cycle[] = this.cycles.filter(
            (cycle: Cycle) =>
              cycle.engineeringcode != null && cycle.year != null
          );

          const regionCall = this.writerService.processRecord(
            xRegions,
            "bodystyle_region",
            "POST"
          );
          observableCalls.push(regionCall);
          const cycleCall = this.writerService.processRecord(
            xCycles,
            "cycle",
            "POST"
          );
          observableCalls.push(cycleCall);
          //Subscribe to all observables
          forkJoin(observableCalls).subscribe({
            complete: () => {
              this.isLoading = false;
              this.dialog.open(ErrorHandlerDialogComponent, {
                data: { message: Messages.successRefresh, status: "Success" },
              });
              this.dialogRef.close();
            },
            error: (error: any) => {
              this.dialog.open(ErrorHandlerDialogComponent, {
                data: {
                  message:
                    "Error saving Body Cycle Record </br>" +
                    error.message +
                    error.debugMessage,
                  status: error.status,
                },
              });
            },
          });
        },
        error: (error: any) => {
          this.dialog.open(ErrorHandlerDialogComponent, {
            data: {
              message:
                "Error saving Body Cycle Record </br>" + error.error === null
                  ? error.message
                  : error.error.message,
              status: error.error === null ? error.status : error.error.status,
            },
          });
        },
      });
  }

  public delete(): void {
    this.confirmDialog = this.dialog.open(ConfirmDeletionDialogComponent, {
      data: {
        recordId: this.bodystyle.id,
        recordName: this.bodystyle.bodystyle,
        recordType: "bodystyle",
        vehicleId: this.bodystyle.vehicleid,
      },
    });

    this.confirmDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close();
      }
    });
  }

  public isValid(): boolean {
    let deleteCheck1 = false;
    let deleteCheck2 = false;
    let deleteCheck3 = false;
    let deleteCheck4 = false;

    this.cycle1 = false;
    this.cycle2 = false;
    this.cycle3 = false;
    this.cycle4 = false;
    this.cycle5 = false;
    // If statements to check if cycle year is greater than previous cycle year
    this.updateCycleStatus();

    // MAKES IT POSSIBLE TO SELECT BLANK WITHOUT HAVING TO ENTER A NUMBER REMOVE THIS SECTION ONCE DELETE FULLY INCORPORATED
    ({ deleteCheck1, deleteCheck2, deleteCheck3, deleteCheck4 } =
      this.updateDeleteChecks(
        deleteCheck1,
        deleteCheck2,
        deleteCheck3,
        deleteCheck4
      ));

    // loops through checklist of regions and states whether one is checked or not
    this.regionControl = false;
    for (const region of this.regions) {
      if (region.value === true) {
        this.regionControl = true;
      }
    }

    const res = this.getValidity(
      deleteCheck1,
      deleteCheck2,
      deleteCheck3,
      deleteCheck4
    );

    return res;
  }

  private getValidity(
    deleteCheck1: boolean,
    deleteCheck2: boolean,
    deleteCheck3: boolean,
    deleteCheck4: boolean
  ): boolean {
    return !(
      this.bodyControl.invalid ||
      !this.regionControl ||
      !this.cycle5 ||
      (!this.cycle2 && this.cycles[1].year != null) ||
      (!this.cycle3 && this.cycles[2].year != null) ||
      (!this.cycle4 && this.cycles[3].year != null) ||
      deleteCheck1 ||
      deleteCheck2 ||
      deleteCheck3 ||
      deleteCheck4 ||
      this.cycles.filter(
        (cycle: Cycle) => cycle.engineeringcode == null && cycle.year != null
      ).length > 0 ||
      this.cycles[0].engineeringcode == null ||
      (this.cycles[0].year == null && this.cycles[0].engineeringcode !== "") ||
      this.cycles[4].engineeringcode == null ||
      (this.cycles[4].year == null && this.cycles[4].engineeringcode !== "")
    );
  }

  private updateCycleStatus() {
    if (
      this.cycles[1].year !== null &&
      this.cycles[1].year > this.cycles[0].year
    ) {
      this.cycle2 = true;
    }
    if (
      this.cycles[2].year !== null &&
      this.cycles[2].year > this.cycles[1].year &&
      this.cycles[2].year > this.cycles[0].year
    ) {
      this.cycle3 = true;
    }
    if (
      this.cycles[3].year !== null &&
      this.cycles[3].year > this.cycles[2].year &&
      this.cycles[3].year > this.cycles[1].year &&
      this.cycles[3].year > this.cycles[0].year
    ) {
      this.cycle4 = true;
    }
    if (
      (this.cycles[4].year !== null &&
        this.cycles[4].year > this.cycles[3].year &&
        this.cycles[4].year > this.cycles[2].year &&
        this.cycles[4].year > this.cycles[1].year &&
        this.cycles[4].year > this.cycles[0].year) ||
      this.cycles[4].engineeringcode === ""
    ) {
      this.cycle5 = true;
    }
  }

  private updateDeleteChecks(
    deleteCheck1: boolean,
    deleteCheck2: boolean,
    deleteCheck3: boolean,
    deleteCheck4: boolean
  ) {
    if (
      this.cycles[1].engineeringcode != null &&
      this.cycles[1].engineeringcode !== "" &&
      this.cycles[1].year == null
    ) {
      deleteCheck1 = true;
    } else if (this.cycles[1].engineeringcode === "") {
      deleteCheck1 = false;
    }
    if (
      this.cycles[2].engineeringcode != null &&
      this.cycles[2].engineeringcode !== "" &&
      this.cycles[2].year == null
    ) {
      deleteCheck2 = true;
    } else if (this.cycles[2].engineeringcode === "") {
      deleteCheck2 = false;
    }
    if (
      this.cycles[3].engineeringcode != null &&
      this.cycles[3].engineeringcode !== "" &&
      this.cycles[3].year == null
    ) {
      deleteCheck3 = true;
    } else if (this.cycles[3].engineeringcode === "") {
      deleteCheck3 = false;
    }
    if (
      this.cycles[4].engineeringcode != null &&
      this.cycles[4].engineeringcode !== "" &&
      this.cycles[4].year == null
    ) {
      deleteCheck4 = true;
    } else if (this.cycles[4].engineeringcode === "") {
      deleteCheck4 = false;
    }
    return { deleteCheck1, deleteCheck2, deleteCheck3, deleteCheck4 };
  }

  private yearMessage(cYear: number): string {
    const yearMessage = "Year needs to be greater";
    if (cYear === 4 && !this.cycle5) {
      return yearMessage;
    } else if (cYear === 3 && !this.cycle4) {
      return yearMessage;
    } else if (cYear === 2 && !this.cycle3) {
      return yearMessage;
    } else if (cYear === 1 && !this.cycle2) {
      return yearMessage;
    } else {
      return "";
    }
  }

  // Returns the proper error message for the appropriate cycle year and if cycle is greater the previous year
  public getReq(cYear: number): string {
    if (
      this.cycles[cYear].engineeringcode != null &&
      this.cycles[cYear].engineeringcode !== "" &&
      this.cycles[cYear].year != null
    ) {
      return this.yearMessage(cYear);
    } else {
      if (
        (cYear === 0 || cYear === 4) &&
        this.cycles[cYear].engineeringcode !== ""
      ) {
        return " Required";
      } else {
        return "";
      }
    }
  }

  public getMinValue(year: number): number {
    for (let i = year; i < this.counter.length; ++i) {
      if (this.cycles[year].year !== null) {
        this.counter[i] = this.cycles[year].year;
      }
    }
    if (year === 0) {
      return 1980;
    } else {
      return this.counter[year - 1];
    }
  }

  public getIdCycle(idNum: number): string {
    return "cycle" + (idNum + 1);
  }

  public getIdYear(idNum: number): string {
    return "cycleYear" + (idNum + 1);
  }

  public updateVehicle(): void {
    const vehicle: string[] = String(this.vehicleControl.value).split("-");
    if (vehicle[0] != null && vehicle[0] !== "") {
      this.readerService
        .getVehicleData(vehicle[0])
        .subscribe((x: Vehicle) => (this.vehicle = x));
    }
  }

  public verifyCycleYear(cycle: Cycle): void {
    if (cycle.engineeringcode === null || cycle.engineeringcode === "") {
      cycle.year = null;
    }
  }
}

export class BodyStyle {
  id: string;
  vehicleid: string;
  bodystyle: string;
  lead: boolean;
  status: string;
  created: string;
  createdby: string;
  nameplate: string;
}
